import { Box, Divider, Stack, useMediaQuery } from "@mui/material";
import { useLocation } from "react-router-dom";
import { Link } from "react-router-dom";
import { Logo } from "../../components/Logo";
import { SimpleSidebarLink } from "./SidebarLinks/SimpleSidebarLink";
import { SidebarLinkPathTypes, SidebarLinkTypes } from "./LinkTypes";
import ComplexSidebarLink from "./SidebarLinks/ComplexSidebarLink";
import DashboardIcon from "@mui/icons-material/Dashboard";
import { KeyIcon, PersonIcon } from "../../assets/icons/ActionIcons";
import { ArrowCircleLeftIcon, GroupIcon, GroupViewIcon, HomeIcon, ServiceModulesIcon } from "../../assets/icons/Icons";
import {
  CollapseToggleButton,
  LogoWrapper,
  NavBox,
  NavDrawerStyles,
  SideNavWrapper,
} from "./Layout.styles";
import { keyValueStorage } from "../../common/logic/KeyValueStore";
import { useFeatureFlags } from "../../store/selectors/loginMetadataSelector";
import { useEffect, useMemo, useRef, useState } from "react";
import { PermissionEntityType } from "../../types/permissionType";
import { usePermissionChecker } from "../../hooks/usePermissionChecker";
import { NotificationsOutlined, History } from "@mui/icons-material";

interface Props {
  open: boolean;
  onClose: () => void;
  collapsed: boolean;
  setCollapsed: (val: boolean) => void;
}

export interface FilteredListType {
  subTypeNames: string[];
}

export const SideNav: React.FC<Props> = ({ open, onClose, collapsed, setCollapsed }) => {
  const pathname = useLocation().pathname;
  const { userHasPermission, userHasAnyPermission } = usePermissionChecker();
  const userRole = keyValueStorage.getObject<string>("userRole", "");
  const lgUp = useMediaQuery((theme: any) => theme.breakpoints.up("lg"));
  const activePathname = useMemo(() => pathname.split("/").slice(2).join("/"), [pathname]);
  let featureFlagsStore = useFeatureFlags()
  const [showText, setShowText] = useState(true);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const hasDashboardPermission = userHasPermission(PermissionEntityType.General, "Dashboard");
  const hasIncentivationPermission = userHasAnyPermission(PermissionEntityType.ActionModules, "Incentivate");
  const hasEmployeesPermission = userHasPermission(PermissionEntityType.General, "AccountManagement");
  const hasGroupsPermission = userHasAnyPermission(PermissionEntityType.Groups, "View");
  const hasActionModulesPermission = userHasAnyPermission(PermissionEntityType.ActionModules, "View");
  const hasServiceModulesPermission = userHasAnyPermission(PermissionEntityType.ServiceModules, "View");
  // const hasFileExchangePermission = hasPermission(PermissionEntityType.General, "FileExchange");
  const hasWalletPermission = userHasPermission(PermissionEntityType.General, "CompanyWallet");
  const hasBillingPermission = userHasPermission(PermissionEntityType.General, "Billing");
  const hasCompanyManagementPermission = userHasPermission(PermissionEntityType.General, "CompanyManagement");
  const hasPermissionsViewPermission = userHasPermission(PermissionEntityType.Permissions, "View");
  const hasGenericInAppNotificationsPermission = userHasPermission(PermissionEntityType.General, "GenericInAppNotifications");
  const hasCustomizationPermission = userHasPermission(PermissionEntityType.General, "Customization");
  const hasAuditLogPermission = userHasPermission(PermissionEntityType.General, "AuditLog");

  const hasSubCompanyPermissions =
    userHasAnyPermission(PermissionEntityType.SubCompanies, "View") ||
    userHasAnyPermission(PermissionEntityType.SubCompanies, "Edit") ||
    userHasAnyPermission(PermissionEntityType.SubCompanies, "Create") ||
    userHasAnyPermission(PermissionEntityType.SubCompanies, "Delete");

  useEffect(() => {
    if (!wrapperRef.current) return;
    const handleTransitionEnd = (event: TransitionEvent) => {
      if (event.propertyName === "width") {
        setShowText(!collapsed);
      }
    };
    wrapperRef.current.addEventListener("transitionend", handleTransitionEnd);
    if (collapsed) setShowText(false);
    return () => {
      wrapperRef.current?.removeEventListener("transitionend", handleTransitionEnd);
    };
  }, [collapsed]);

  useEffect(() => {
    if (!lgUp && collapsed) {
      setCollapsed(false);
    }
  }, [lgUp]);

  const content = (
    <SideNavWrapper ref={wrapperRef} collapsed={collapsed}>
      <LogoWrapper>
        <Box
          component={Link}
          to="/"
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 60,
            visibility: collapsed ? "hidden" : "visible"
          }}
        >
          <Logo/> 
        </Box>
      </LogoWrapper>
      <Divider />
      <NavBox component="nav">
        <Stack component="ul">
          {userRole === "CompanyManagement" && (
            <>
              {featureFlagsStore.includes("DashboardPage") && hasDashboardPermission && (
                <SimpleSidebarLink
                  isActive={activePathname === SidebarLinkPathTypes.dashboard}
                  icon={<DashboardIcon />}
                  linkName={SidebarLinkTypes.dashboard}
                  path={SidebarLinkPathTypes.dashboard}
                  showText={showText}
                  collapsed={collapsed}
                />
              )}
              {hasIncentivationPermission && (
                <ComplexSidebarLink
                  linkName={SidebarLinkTypes.incentivation}
                  activePathname={activePathname}
                  collapsed={collapsed}
                  showText={showText}
                />
              )}
              {hasEmployeesPermission && (
                <SimpleSidebarLink
                  isActive={activePathname === SidebarLinkPathTypes.employees}
                  icon={<PersonIcon />}
                  linkName={SidebarLinkTypes.employees}
                  path={SidebarLinkPathTypes.employees}
                  showText={showText}
                  collapsed={collapsed}
                />
              )}
              {hasGroupsPermission && (
                <SimpleSidebarLink
                  isActive={activePathname === SidebarLinkPathTypes.groups}
                  icon={<GroupIcon />}
                  linkName={SidebarLinkTypes.groups}
                  path={SidebarLinkPathTypes.groups}
                  showText={showText}
                  collapsed={collapsed}
                />
              )}
              {hasActionModulesPermission && (
                <SimpleSidebarLink
                  isActive={activePathname === SidebarLinkPathTypes.actionModules}
                  icon={<GroupViewIcon />}
                  linkName={SidebarLinkTypes.actionModules}
                  path={SidebarLinkPathTypes.actionModules}
                  showText={showText}
                  collapsed={collapsed}
                />
              )}
              {hasServiceModulesPermission && (
                <SimpleSidebarLink
                  isActive={activePathname === SidebarLinkPathTypes.serviceModules}
                  icon={<ServiceModulesIcon />}
                  linkName={SidebarLinkTypes.serviceModules}
                  path={SidebarLinkPathTypes.serviceModules}
                  showText={showText}
                  collapsed={collapsed}
                />
              )}
              {hasWalletPermission && (
                <ComplexSidebarLink
                  linkName={SidebarLinkTypes.wallet}
                  activePathname={activePathname}
                  collapsed={collapsed}
                  showText={showText}
                />
              )}

              {/* Making the Document Management invisible until further instruction */}
              {/* {filteredLinks.subTypeNames.includes("CertificatesAndTraining") &&
                <ComplexSidebarLink
                  linkName={SidebarLinkTypes.documentManagement}
                  activePathname={activePathname}
                  filteredLinks={filteredLinks.subTypeNames}
                />
              } */}
              {featureFlagsStore.includes("BillingPage") && hasBillingPermission && (
                <ComplexSidebarLink
                  linkName={SidebarLinkTypes.analysisFiles}
                  activePathname={activePathname}
                  collapsed={collapsed}
                  showText={showText}
                />
              )}
              {(hasCompanyManagementPermission || hasSubCompanyPermissions) && (
                <SimpleSidebarLink
                  isActive={activePathname === SidebarLinkPathTypes.companyManagement}
                  icon={<HomeIcon />}
                  linkName={SidebarLinkTypes.companyManagement}
                  path={SidebarLinkPathTypes.companyManagement}
                  showText={showText}
                  collapsed={collapsed}
                />
              )}
              {hasPermissionsViewPermission && (
                <SimpleSidebarLink
                  isActive={activePathname === SidebarLinkPathTypes.permissions}
                  icon={<KeyIcon />}
                  linkName={SidebarLinkTypes.permissions}
                  path={SidebarLinkPathTypes.permissions}
                  showText={showText}
                  collapsed={collapsed}
                />
              )}
              {hasGenericInAppNotificationsPermission && (
                <SimpleSidebarLink
                  isActive={activePathname === SidebarLinkPathTypes.inAppNotifications}
                  icon={<NotificationsOutlined />}
                  linkName={SidebarLinkTypes.inAppNotifications}
                  path={SidebarLinkPathTypes.inAppNotifications}
                  showText={showText}
                  collapsed={collapsed}
                />
              )}
              {featureFlagsStore.includes("AppCustomizationsPage") && hasCustomizationPermission && (
                <ComplexSidebarLink
                  linkName={SidebarLinkTypes.configuration}
                  activePathname={activePathname}
                  collapsed={collapsed}
                  showText={showText}
                />
              )}
              {hasAuditLogPermission && (
                <SimpleSidebarLink
                  isActive={activePathname === SidebarLinkPathTypes.auditLogs}
                  icon={<History />}
                  linkName={SidebarLinkTypes.auditLogs}
                  path={SidebarLinkPathTypes.auditLogs}
                  showText={showText}
                />
              )}
            </>
          )}
          {userRole === "Administrator" && (
            <ComplexSidebarLink
              linkName={SidebarLinkTypes.superAdmin}
              activePathname={activePathname}
              showText={showText}
            />
          )}
        </Stack>
      </NavBox>
      <Divider />
      {lgUp && (
        <CollapseToggleButton
          collapsed={collapsed}
          onClick={() => setCollapsed(!collapsed)}
          sx={{
            transform: collapsed ? "translateX(50%) rotate(180deg)" : "translateX(50%)",
            transition: "transform 0.5s ease",
          }}
        >
          <ArrowCircleLeftIcon />
        </CollapseToggleButton>
      )}
    </SideNavWrapper>
  );

  if (lgUp) {
    return (
      <NavDrawerStyles
        anchor="left"
        open={open}
        variant="permanent"
        collapsed={collapsed}
      >
        {content}
      </NavDrawerStyles>
    );
  }

  return (
    <NavDrawerStyles
      anchor="left"
      onClose={onClose}
      open={open}
      variant="temporary"
    >
      {content}
    </NavDrawerStyles>
  );
}
